import type { RouteLocationRaw } from 'vue-router'
import {
  buildApiUrl,
  buildApiUrlWithQuery,
  getAuthHeaders,
  getCurrentRegion,
  valtFetch,
} from '@/utils/api'
import { getUserRole } from '@/utils/userRoleUtils'
import { VehicleApiService } from '@/services/vehicleApiService'
import { contractorAdminApi } from '@/services/contractorService'
import {
  TrackingDeviceApiService,
  telematicsProviderLabel,
  normalizeTelematicsProviderKey,
} from '@/services/trackingDeviceApiService'
import { PLATFORM_PARTNER_LABEL } from '@/config/fleetConstants'
import type {
  GlobalSearchEntityType,
  GlobalSearchGroup,
  GlobalSearchResult,
} from '@/types/globalSearch'
import {
  GLOBAL_SEARCH_ENTITY_ICONS,
  GLOBAL_SEARCH_ENTITY_LABELS,
} from '@/types/globalSearch'

const MAX_PER_GROUP = 6
const MIN_QUERY_LENGTH = 2
const CACHE_TTL_MS = 120_000

type ListCacheEntry = { data: unknown; ts: number }
const listCache = new Map<string, ListCacheEntry>()

function isRoleAllowed(allowedRoles?: string[]): boolean {
  if (!allowedRoles?.length) return true
  const role = getUserRole()
  const effectiveRole = role === 'client_portal' ? 'admin' : role
  return allowedRoles.includes(effectiveRole)
}

function matchesQuery(value: unknown, query: string): boolean {
  if (value == null || value === '') return false
  return String(value).toLowerCase().includes(query)
}

function objectMatches(
  obj: Record<string, unknown>,
  fields: string[],
  query: string,
): boolean {
  const q = query.toLowerCase().trim()
  return fields.some((field) => matchesQuery(obj[field], q))
}

async function fetchCached<T>(key: string, fetcher: () => Promise<T>): Promise<T> {
  const cached = listCache.get(key)
  if (cached && Date.now() - cached.ts < CACHE_TTL_MS) {
    return cached.data as T
  }
  const data = await fetcher()
  listCache.set(key, { data, ts: Date.now() })
  return data
}

async function fetchJson(url: string): Promise<unknown> {
  const res = await valtFetch(url, { headers: getAuthHeaders() })
  if (!res.ok) return null
  return res.json()
}

function makeGroup(
  type: GlobalSearchEntityType,
  results: GlobalSearchResult[],
): GlobalSearchGroup | null {
  if (!results.length) return null
  return {
    type,
    label: GLOBAL_SEARCH_ENTITY_LABELS[type],
    icon: GLOBAL_SEARCH_ENTITY_ICONS[type],
    results: results.slice(0, MAX_PER_GROUP),
  }
}

function extractDriverList(payload: unknown): Record<string, unknown>[] {
  if (Array.isArray(payload)) return payload
  if (!payload || typeof payload !== 'object') return []
  const obj = payload as Record<string, unknown>
  if (Array.isArray(obj.drivers)) return obj.drivers as Record<string, unknown>[]
  if (Array.isArray(obj.data)) return obj.data as Record<string, unknown>[]
  return []
}

async function searchDrivers(query: string): Promise<GlobalSearchResult[]> {
  const region = getCurrentRegion()
  const data = await fetchCached(`our-drivers:${region}`, () =>
    fetchJson(buildApiUrlWithQuery('/our-drivers', { region })),
  )
  const drivers = extractDriverList(data)
  const q = query.toLowerCase().trim()

  return drivers
    .filter((d) =>
      objectMatches(d, [
        'id',
        'name',
        'first_name',
        'last_name',
        'phone_number',
        'email',
        'license_number',
        'driver_uuid',
        'uuid',
      ], q),
    )
    .map((d) => {
      const id = String(d.id ?? '')
      const name =
        String(d.name || '').trim() ||
        [d.first_name, d.last_name].filter(Boolean).join(' ').trim() ||
        `Driver ${id}`
      return {
        id: `driver-${id}`,
        type: 'driver' as const,
        title: name,
        subtitle: String(d.phone_number || d.email || '').trim() || undefined,
        meta: id ? `ID ${id}` : undefined,
        route: { name: 'our-driver-single', params: { driverId: id } },
      }
    })
}

async function searchPlatformPartnerDrivers(query: string): Promise<GlobalSearchResult[]> {
  const region = getCurrentRegion()
  const uberPayload = await fetchCached(`platform-partner-drivers:${region}`, () =>
    fetchJson(buildApiUrlWithQuery('/driver/valt/active', { region })),
  )

  const drivers = extractDriverList(uberPayload)
  const q = query.toLowerCase().trim()

  return drivers
    .filter((d) =>
      objectMatches(d, [
        'driver_id',
        'driver_uuid',
        'name',
        'phone_number',
        'email',
        'id',
      ], q),
    )
    .map((d) => {
      const partnerId = String(d.driver_id || d.driver_uuid || d.id || '').trim()
      const valtId = String(d.valt_driver_id ?? d.our_driver_id ?? '').trim()
      const name = String(d.name || '').trim() || `Driver ${partnerId}`
      return {
        id: `platform-partner-driver-${partnerId}`,
        type: 'platform_partner_driver' as const,
        title: name,
        subtitle: String(d.phone_number || d.email || '').trim() || undefined,
        meta: [
          partnerId ? `${PLATFORM_PARTNER_LABEL} ID ${partnerId}` : null,
          valtId ? `valtOS ID ${valtId}` : null,
        ]
          .filter(Boolean)
          .join(' · ') || undefined,
        route: { name: 'our-drivers', query: { status: 'active' } },
      }
    })
}

async function searchAssets(query: string): Promise<GlobalSearchResult[]> {
  try {
    const apiResponse = await VehicleApiService.searchVehicles(query, { limit: MAX_PER_GROUP })
    const vehicles = (apiResponse.data ?? []).filter(
      (v) => !v.uber_vehicle_uuid && !(v as Record<string, unknown>).uber_vehicle_uuid,
    )
    return vehicles.map((v) => {
      const id = String(v.id)
      const plate = String(v.number_plate || v.licensePlate || '').trim()
      const vin = String(v.vin || '').trim()
      const title = plate || String(v.name || '').trim() || `Asset ${id}`
      const subtitle = [v.car_make || (v as Record<string, unknown>).make, v.model, vin]
        .filter(Boolean)
        .join(' · ')
      return {
        id: `asset-${id}`,
        type: 'asset' as const,
        title,
        subtitle: subtitle || undefined,
        meta: `Asset ID ${id}${vin ? ` · VIN ${vin}` : ''}`,
        route: { name: 'our-vehicle-single', params: { vehicleId: id } },
      }
    })
  } catch {
    return []
  }
}

async function searchPlatformPartnerAssets(query: string): Promise<GlobalSearchResult[]> {
  try {
    const q = query.toLowerCase().trim()
    const apiResponse = await VehicleApiService.getUberVehicles({ per_page: 500 })
    const vehicles = (apiResponse.data ?? []).filter((v) => {
      const ext = v as Record<string, unknown>
      return objectMatches(ext, [
        'id',
        'number_plate',
        'licensePlate',
        'vin',
        'model',
        'car_make',
        'make',
        'uber_vehicle_uuid',
        'vehicleId',
      ], q)
    })

    return vehicles.slice(0, MAX_PER_GROUP).map((v) => {
      const ext = v as Record<string, unknown>
      const id = String(v.id)
      const partnerUuid = String(v.uber_vehicle_uuid ?? ext.uber_vehicle_uuid ?? '').trim()
      const plate = String(v.number_plate || v.licensePlate || '').trim()
      const vin = String(v.vin || '').trim()
      const title = plate || String(v.name || '').trim() || `Asset ${id}`
      const subtitle = [v.car_make || ext.make, v.model, vin].filter(Boolean).join(' · ')
      return {
        id: `platform-partner-asset-${id}`,
        type: 'platform_partner_asset' as const,
        title,
        subtitle: subtitle || undefined,
        meta: [
          `Asset ID ${id}`,
          partnerUuid ? `${PLATFORM_PARTNER_LABEL} ID ${partnerUuid}` : null,
        ]
          .filter(Boolean)
          .join(' · '),
        route: { name: 'our-vehicle-single', params: { vehicleId: id } },
      }
    })
  } catch {
    return []
  }
}

async function searchTrackingDevices(query: string): Promise<GlobalSearchResult[]> {
  const region = getCurrentRegion()
  const data = await fetchCached(`tracking-devices:${region}`, () =>
    TrackingDeviceApiService.list({ per_page: 500 }),
  )

  const devices = data.devices ?? []
  const q = query.toLowerCase().trim()

  return devices
    .filter((d) =>
      objectMatches(d as unknown as Record<string, unknown>, [
        'id',
        'telemetry_id',
        'name',
        'model',
        'asset_number_plate',
        'license_plate',
        'vin',
        'telematics_provider',
        'asset_id',
      ], q),
    )
    .map((d) => {
      const telemetryId = String(d.telemetry_id || '').trim()
      const deviceId = String(d.id)
      const plate = String(d.asset_number_plate || d.license_plate || '').trim()
      const providerKey = normalizeTelematicsProviderKey(d.telematics_provider)
      const title = String(d.name || plate || telemetryId || `Device ${deviceId}`).trim()
      const subtitle = [
        telematicsProviderLabel(providerKey),
        d.model,
        plate && plate !== title ? plate : null,
        d.asset_id ? `Asset ID ${d.asset_id}` : null,
      ]
        .filter(Boolean)
        .join(' · ')
      return {
        id: `tracking-device-${deviceId}`,
        type: 'tracking_device' as const,
        title,
        subtitle: subtitle || undefined,
        meta: [
          `Device ID ${deviceId}`,
          telemetryId ? `Telemetry ${telemetryId}` : null,
          providerKey ? `Provider ${providerKey}` : null,
        ]
          .filter(Boolean)
          .join(' · '),
        route: d.asset_id
          ? { name: 'our-vehicle-single', params: { vehicleId: String(d.asset_id) } }
          : { name: 'tracking-devices' },
      }
    })
}

async function searchBikes(query: string): Promise<GlobalSearchResult[]> {
  const region = getCurrentRegion()
  const data = (await fetchJson(
    buildApiUrlWithQuery('/bikes/search', { search: query, q: query, region }),
  )) as { data?: Record<string, unknown>[]; bikes?: Record<string, unknown>[] } | Record<string, unknown>[] | null

  const bikes = Array.isArray(data) ? data : (data?.data ?? data?.bikes ?? [])

  return bikes.slice(0, MAX_PER_GROUP).map((b) => {
    const deviceId = String(b.deviceId ?? b.device_id ?? b.id ?? '').trim()
    const bikeId = String(b.id ?? deviceId).trim()
    const plate = String(b.licensePlate ?? b.number_plate ?? '').trim()
    const title = String(b.name || plate || deviceId || 'Bike').trim()
    return {
      id: `bike-${bikeId}`,
      type: 'bike' as const,
      title,
      subtitle: plate && plate !== title ? plate : String(b.model || b.vin || '').trim() || undefined,
      meta: [
        bikeId ? `Bike ID ${bikeId}` : null,
        deviceId ? `Telemetry ${deviceId}` : null,
      ]
        .filter(Boolean)
        .join(' · ') || undefined,
      route: { name: 'our-vehicles', query: { filter: 'bike' } },
    }
  })
}

async function searchClients(query: string): Promise<GlobalSearchResult[]> {
  const clients = await fetchCached('clients', () => contractorAdminApi.list({ per_page: 500 }))
  const q = query.toLowerCase().trim()

  return (clients as Record<string, unknown>[])
    .filter((c) =>
      objectMatches(c, [
        'id',
        'company_name',
        'contact_person',
        'email',
        'phone',
        'name',
      ], q),
    )
    .map((c) => {
      const id = String(c.id ?? '')
      const title = String(c.company_name || c.name || `Client ${id}`).trim()
      return {
        id: `client-${id}`,
        type: 'client' as const,
        title,
        subtitle: String(c.contact_person || c.email || '').trim() || undefined,
        meta: id ? `Client ID ${id}` : undefined,
        route: { name: 'client-detail', params: { id } },
      }
    })
}

async function searchUsers(query: string): Promise<GlobalSearchResult[]> {
  const users = (await fetchCached('users', async () => {
    const data = await fetchJson(buildApiUrl('/user'))
    return Array.isArray(data) ? data : (data as { data?: unknown[] })?.data ?? []
  })) as Record<string, unknown>[]

  const q = query.toLowerCase().trim()

  return users
    .filter((u) =>
      objectMatches(u, [
        'id',
        'name',
        'first_name',
        'last_name',
        'email',
        'phone_number',
      ], q),
    )
    .map((u) => {
      const id = String(u.id ?? '')
      const name =
        String(u.name || '').trim() ||
        [u.first_name, u.last_name].filter(Boolean).join(' ').trim() ||
        String(u.email || `User ${id}`)
      return {
        id: `user-${id}`,
        type: 'user' as const,
        title: name,
        subtitle: String(u.email || u.phone_number || '').trim() || undefined,
        meta: id ? `User ID ${id}` : undefined,
        route: { path: `/operator-dashboard/${id}` },
      }
    })
}

async function searchTickets(query: string): Promise<GlobalSearchResult[]> {
  const tickets = (await fetchCached('tickets', async () => {
    const data = await fetchJson(buildApiUrl('/tickets'))
    return Array.isArray(data) ? data : (data as { data?: unknown[] })?.data ?? []
  })) as Record<string, unknown>[]

  const q = query.toLowerCase().trim()

  return tickets
    .filter((t) =>
      objectMatches(t, ['id', 'uuid', 'title', 'message', 'status', 'priority'], q),
    )
    .map((t) => {
      const id = String(t.id ?? '')
      const title = String(t.title || `Ticket ${id}`).trim()
      return {
        id: `ticket-${id}`,
        type: 'ticket' as const,
        title,
        subtitle: String(t.message || '').trim().slice(0, 80) || undefined,
        meta: id ? `Ticket ID ${id}` : undefined,
        route: { name: 'ticketsingle', params: { ticketNumber: id } },
      }
    })
}

async function searchApplicants(query: string): Promise<GlobalSearchResult[]> {
  const region = getCurrentRegion()
  const data = (await fetchCached(`applicants:${region}`, async () => {
    return fetchJson(buildApiUrlWithQuery('/driver/applicants', { region }))
  })) as { applicants?: Record<string, unknown>[]; data?: Record<string, unknown>[] } | Record<string, unknown>[] | null

  const applicants = Array.isArray(data) ? data : (data?.applicants ?? data?.data ?? [])
  const q = query.toLowerCase().trim()

  return applicants
    .filter((a) =>
      objectMatches(a, [
        'id',
        'driver_id',
        'name',
        'phone_number',
        'email',
        'phone',
      ], q),
    )
    .map((a) => {
      const applicantId = String(a.id ?? '')
      const driverId = String(a.driver_id ?? '').trim()
      const name = String(a.name || '').trim() || `Applicant ${applicantId}`
      const route: RouteLocationRaw = driverId
        ? { name: 'applicant-detail-by-driver-id', params: { driverId } }
        : { name: 'applicant-detail', params: { id: applicantId } }
      return {
        id: `applicant-${applicantId || driverId}`,
        type: 'applicant' as const,
        title: name,
        subtitle: String(a.phone_number || a.phone || a.email || '').trim() || undefined,
        meta: [
          applicantId ? `Applicant ID ${applicantId}` : null,
          driverId ? `Driver ID ${driverId}` : null,
        ]
          .filter(Boolean)
          .join(' · ') || undefined,
        route,
      }
    })
}

type SearchProvider = {
  type: GlobalSearchEntityType
  allowedRoles?: string[]
  search: (query: string) => Promise<GlobalSearchResult[]>
}

const SEARCH_PROVIDERS: SearchProvider[] = [
  { type: 'asset', search: searchAssets },
  { type: 'platform_partner_asset', search: searchPlatformPartnerAssets },
  { type: 'tracking_device', search: searchTrackingDevices },
  { type: 'driver', search: searchDrivers },
  { type: 'platform_partner_driver', search: searchPlatformPartnerDrivers },
  { type: 'bike', search: searchBikes },
  {
    type: 'client',
    allowedRoles: ['admin', 'manager', 'org_admin'],
    search: searchClients,
  },
  {
    type: 'user',
    allowedRoles: ['admin', 'manager', 'org_admin', 'owner'],
    search: searchUsers,
  },
  {
    type: 'ticket',
    allowedRoles: ['admin', 'operator', 'manager', 'org_admin', 'owner', 'recruiter', 'support_agent'],
    search: searchTickets,
  },
  {
    type: 'applicant',
    allowedRoles: ['recruiter', 'admin', 'org_admin', 'owner'],
    search: searchApplicants,
  },
]

export async function runGlobalSearch(query: string): Promise<GlobalSearchGroup[]> {
  const trimmed = query.trim()
  if (trimmed.length < MIN_QUERY_LENGTH) return []

  const providers = SEARCH_PROVIDERS.filter((p) => isRoleAllowed(p.allowedRoles))
  const settled = await Promise.allSettled(providers.map((p) => p.search(trimmed)))

  const groups: GlobalSearchGroup[] = []
  settled.forEach((result, index) => {
    if (result.status !== 'fulfilled') return
    const group = makeGroup(providers[index].type, result.value)
    if (group) groups.push(group)
  })

  return groups
}

export function clearGlobalSearchCache(): void {
  listCache.clear()
}
